const chapter = {
  id: 'electrochemistry',
  slug: 'electrochemistry',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Electrochemistry',
        hindi: 'विद्युत रसायन',
  },
  estReadMinutes: 70,
  difficulty: {
    english: 'Hard',
        hindi: 'कठिन',
  },
  weightage: {
    english: '2–3 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/एनईईटी में 2-3 प्रश्न',
  },
  syllabusTags: ['Physical Chemistry', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'galvanic',
      title: {
        english: 'Galvanic (Voltaic) Cells',
        hindi: 'गैल्वेनिक (वोल्टेइक) सेल',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Converts chemical → electrical energy via spontaneous redox.',
        hindi: 'सहज रेडॉक्स के माध्यम से रासायनिक → विद्युत ऊर्जा को परिवर्तित करता है। **एनोड (-)**: ऑक्सीकरण। **कैथोड (+)**: कमी। पारंपरिक धारा कैथोड → बाहरी रूप से एनोड प्रवाहित होती है; इलेक्ट्रॉन एनोड → कैथोड को बाह्य रूप से प्रवाहित करते हैं।',
            },
            {
              english: '**Anode (−)**: oxidation. **Cathode (+)**: reduction. Conventional current flows cathode → anode externally; electrons flow anode → cathode externally.',
        hindi: '**एनोड (-)**: ऑक्सीकरण। **कैथोड (+)**: कमी। पारंपरिक धारा कैथोड → बाहरी रूप से एनोड प्रवाहित होती है; इलेक्ट्रॉन एनोड → कैथोड को बाह्य रूप से प्रवाहित करते हैं।',
            },
            {
              english: '**Salt bridge** (KCl/KNO₃/NH₄NO₃ in agar gel): completes the circuit, maintains electrical neutrality, prevents liquid-junction potential.',
        hindi: '**नमक पुल** (अगर जेल में KCl/KNO₃/NH₄NO₃): सर्किट को पूरा करता है, विद्युत तटस्थता बनाए रखता है, तरल-जंक्शन क्षमता को रोकता है।',
            },
            {
              english: 'Cell representation (IUPAC): **anode | anode soln (conc) || cathode soln (conc) | cathode**. Example: $Zn | Zn^{2+}(1\\,M) || Cu^{2+}(1\\,M) | Cu$.',
        hindi: 'सेल प्रतिनिधित्व (आईयूपीएसी): **एनोड | एनोड सोलन (सांद्र) || कैथोड सोलन (कॉन्स) | कैथोड**. उदाहरण: $Zn | Zn^{2+}(1\\,M) || Cu^{2+}(1\\,M) | Cu$.',
            },
            {
              english: 'EMF of cell: $E_{\\text{cell}} = E_{\\text{cathode}} - E_{\\text{anode}}$ (both as reduction potentials).',
        hindi: 'सेल का EMF: $E_{\\text{कोशिका}} = E_{\\text{cathode}} - E_{\\text{anode}}$ (दोनों कमी क्षमता के रूप में)।',
            },
          ],
        },
        {
          type: 'definition',
          term: {
            english: 'Standard hydrogen electrode (SHE)',
        hindi: 'मानक हाइड्रोजन इलेक्ट्रोड (SHE)',
          },
          body: {
            english: 'Pt | H₂(1 bar) | H⁺(1 M). Assigned $E^\\circ = 0$ V at all temperatures. Reference for all standard electrode potentials.',
        hindi: 'पीटी | H₂(1 बार) | एच⁺(1 एम). सभी तापमानों पर $E^\\circ = 0$ V निर्दिष्ट। सभी मानक इलेक्ट्रोड विभवों के लिए संदर्भ।',
          },
        },
      ],
    },
    {
      id: 'nernst',
      title: {
        english: 'Nernst Equation',
        hindi: 'नर्न्स्ट समीकरण',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Nernst equation',
        hindi: 'नर्नस्ट समीकरण',
          },
          statement: {
            english: `For an electrode reaction $M^{n+} + ne^- \\rightleftharpoons M$:
$E = E^\\circ - \\dfrac{RT}{nF}\\ln Q$. At 298 K (with log instead of ln):
$E = E^\\circ - \\dfrac{0.0591}{n}\\log Q$.
For full cell: $E_{\\text{cell}} = E^\\circ_{\\text{cell}} - \\dfrac{0.0591}{n}\\log Q$.`,
            hindi: 'इलेक्ट्रोड प्रतिक्रिया के लिए $M^{n+} + ne^- \\rightleftharpoons M$: $E = E^\\circ - \\dfrac{RT}{nF}\\ln Q$. 298 K पर (ln के बजाय लॉग के साथ): $E = E^\\circ - \\dfrac{0.0591}{n}\\log Q$. पूर्ण सेल के लिए: $E_{\\text{कोशिका}} = E^\\circ_{\\text{कोशिका}} - \\dfrac{0.0591}{n}\\log Q$।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'At equilibrium: $E_{\\text{cell}} = 0$, $Q = K_c$ ⇒ $\\log K_c = nE^\\circ_{\\text{cell}}/0.0591$.',
        hindi: 'संतुलन पर: $E_{\\text{cell}} = 0$, $Q = K_c$ ⇒ $\\log K_c = nE^\\circ_{\\text{cell}}/0.0591$।',
            },
            {
              english: 'Free energy: $\\Delta G^\\circ = -nFE^\\circ$, $\\Delta G = -nFE$. Spontaneous if $E^\\circ_{\\text{cell}} > 0$.',
        hindi: 'मुफ़्त ऊर्जा: $\\Delta G^\\circ = -nFE^\\circ$, $\\Delta G = -nFE$. सहज यदि $E^\\circ_{\\text{cell}} > 0$.',
            },
            {
              english: 'Faraday: $F = 96485$ C/mol of electrons.',
        hindi: 'फैराडे: इलेक्ट्रॉनों का $F = 96485$ C/mol।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'For Daniell cell, $E^\\circ = 1.10$ V. Find $K_c$ at 298 K.',
        hindi: 'डेनियल सेल के लिए, $E^\\circ = 1.10$ V. 298 K पर $K_c$ खोजें।',
          },
          solution: {
            english: '$\\log K_c = 2 \\times 1.10/0.0591 = 37.23 \\Rightarrow K_c \\approx 1.7\\times 10^{37}$.',
        hindi: '$\\log K_c = 2 \\times 1.10/0.0591 = 37.23 \\Rightarrow K_c \\approx 1.7\\times 10^{37}$.',
          },
        },
      ],
    },
    {
      id: 'conductance',
      title: {
        english: 'Conductance of Electrolytic Solutions',
        hindi: 'विद्युत अपघटन विलयनों की चालकता',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Conductance** $G = 1/R$ (S = $\\Omega^{-1}$). For a cell of cross-section $A$ and length $\\ell$: $R = \\rho \\ell/A$ ⇒ $\\kappa = (1/\\rho) = G(\\ell/A)$, where $\\kappa$ = **conductivity** (S/cm).',
        hindi: '**चालन** $G = 1/R$ (S = $\\Omega^{-1}$). क्रॉस-सेक्शन $A$ और लंबाई $\\ell$ के सेल के लिए: $R = \\rho \\ell/A$ ⇒ $\\kappa = (1/\\rho) = G(\\ell/A)$, जहां $\\kappa$ = **चालकता** (एस/सेमी)।',
            },
            {
              english: 'Cell constant $G^* = \\ell/A$ (cm⁻¹), determined using standard KCl solution.',
        hindi: 'सेल स्थिरांक $G^* = \\ell/A$ (cm⁻¹), मानक KCl समाधान का उपयोग करके निर्धारित किया जाता है। **मोलर चालकता** $G^* = \\ell/A$ (इकाइयाँ: S·cm²/mol जब ³ में, या जब में)।',
            },
            {
              english: '**Molar conductivity** $\\Lambda_m = \\kappa/c$ (units: S·cm²/mol when $c$ in mol/cm³, or $\\kappa\\times 1000/c$ when $c$ in mol/L).',
        hindi: '**मोलर चालकता** $\\Lambda_m = \\kappa/c$ (इकाइयाँ: S·cm²/mol जब $c$ mol/cm³ में, या $\\kappa\\times 1000/c$ जब $c$ mol/L में)।',
            },
            {
              english: 'Variation with concentration: **strong electrolyte** $\\Lambda_m \\to \\Lambda_m^\\circ$ slowly; obeys $\\Lambda_m = \\Lambda_m^\\circ - A\\sqrt{c}$ (**Debye-Hückel-Onsager**). **Weak electrolyte** $\\Lambda_m$ rises sharply on dilution due to increase in degree of dissociation.',
        hindi: 'एकाग्रता के साथ भिन्नता: **मजबूत इलेक्ट्रोलाइट** $\\Lambda_m \\to \\Lambda_m^\\circ$ धीरे-धीरे; $\\Lambda_m = \\Lambda_m^\\circ - A\\sqrt{c}$ (**Debye-Hückel-Onsager**) का पालन करता है। **कमजोर इलेक्ट्रोलाइट** $\\Lambda_m$ पृथक्करण की डिग्री में वृद्धि के कारण तनुकरण पर तेजी से बढ़ता है।',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Kohlrausch\'s law of independent ionic mobility',
        hindi: 'कोहलराउश का स्वतंत्र आयनिक गतिशीलता का नियम',
          },
          statement: {
            english: 'At infinite dilution each ion contributes a constant amount to molar conductivity: $\\Lambda_m^\\circ(\\text{salt}) = \\nu_+ \\lambda_+^\\circ + \\nu_- \\lambda_-^\\circ$. Allows $\\Lambda_m^\\circ$ of weak electrolytes to be computed from those of strong ones.',
        hindi: 'अनंत तनुकरण पर प्रत्येक आयन दाढ़ चालकता में एक स्थिर मात्रा का योगदान देता है: $\\Lambda_m^\\circ(\\text{salt}) = \\nu_+ \\lambda_+^\\circ + \\nu_- \\lambda_-^\\circ$। कमजोर इलेक्ट्रोलाइट्स के $\\Lambda_m^\\circ$ को मजबूत इलेक्ट्रोलाइट्स से गणना करने की अनुमति देता है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Degree of dissociation of weak electrolyte: $\\alpha = \\Lambda_m / \\Lambda_m^\\circ$.',
        hindi: 'कमजोर इलेक्ट्रोलाइट के पृथक्करण की डिग्री: $\\alpha = \\Lambda_m / \\Lambda_m^\\circ$।',
            },
            {
              english: 'Dissociation constant: $K_a = c\\alpha^2/(1-\\alpha)$ (Ostwald).',
        hindi: 'पृथक्करण स्थिरांक: $K_a = c\\alpha^2/(1-\\alpha)$ (ओस्टवाल्ड)।',
            },
          ],
        },
      ],
    },
    {
      id: 'electrolysis',
      title: {
        english: 'Electrolysis & Faraday\'s Laws',
        hindi: 'इलेक्ट्रोलिसिस और फैराडे के नियम',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Faraday\'s laws of electrolysis',
        hindi: 'फैराडे के इलेक्ट्रोलिसिस के नियम',
          },
          statement: {
            english: `**1st**: mass deposited $\\propto$ charge passed: $w = Z\\,Q = Z\\,I t$ (Z = electrochemical equivalent = $M/(nF)$).
**2nd**: when same charge is passed through different electrolytes, masses deposited are in ratio of their **equivalent masses**: $w_1/w_2 = E_1/E_2$.`,
            hindi: '**पहला**: बड़े पैमाने पर जमा $\\propto$ शुल्क पारित: $w = Z\\,Q = Z\\,I t$ (Z = electrochemical equivalent = )। **दूसरा**: जब एक ही चार्ज को अलग-अलग इलेक्ट्रोलाइट्स से गुजारा जाता है, तो जमा हुआ द्रव्यमान उनके **समतुल्य द्रव्यमान** के अनुपात में होता है: $w_1/w_2 = E_1/E_2$।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Charge of one mole of electrons (Faraday): $F = 96485$ C ≈ 96500 C.',
        hindi: 'एक मोल इलेक्ट्रॉन का चार्ज (फैराडे): $F = 96485$ C ≈ 96500 C.',
            },
            {
              english: 'Moles of electrons = $It/F$. Moles of metal deposited = (moles of e⁻)/$n$.',
        hindi: 'electrons = के मोल। धातु के मोल deposited = (moles of e⁻)/. **कैथोड पर डिस्चार्ज ऑर्डर** (aq समाधान): $It/F$ क्षार / जीपी 2 धनायन। **एनोड पर**: $n$ को एक्यू सोल से डिस्चार्ज नहीं किया जाता है; इसके बजाय electrons = या हैलाइड (Cl⁻, Br⁻, I⁻) है।',
            },
            {
              english: '**Discharge order at cathode** (aq solution): $Au^{3+} > Ag^+ > Cu^{2+} > H^+ > $ alkali / Gp 2 cations. **At anode**: $SO_4^{2-}, NO_3^-$ are not discharged from aq sol; instead $H_2O$ or halide (Cl⁻, Br⁻, I⁻) is.',
        hindi: '**कैथोड पर डिस्चार्ज ऑर्डर** (एक्यू समाधान): $Au^{3+} > Ag^+ > Cu^{2+} > H^+ > $ क्षार / जीपी 2 धनायन। **एनोड पर**: $SO_4^{2-}, NO_3^-$ को एक्यू सोल से डिस्चार्ज नहीं किया जाता है; इसके बजाय $H_2O$ या हैलाइड (Cl⁻, Br⁻, I⁻) है।',
            },
            {
              english: 'Examples: electrolysis of aq CuSO₄ with Cu electrodes — Cu refining (anode dissolves, deposits at cathode); electrolysis of aq NaCl — Cl₂ at anode, H₂ at cathode (chlor-alkali).',
        hindi: 'उदाहरण: Cu इलेक्ट्रोड के साथ aq CuSO₄ का इलेक्ट्रोलिसिस - Cu शोधन (एनोड घुल जाता है, कैथोड पर जमा होता है); Aq NaCl का इलेक्ट्रोलिसिस - एनोड पर Cl₂, कैथोड पर H₂ (क्लोर-क्षार)।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Current of 5 A is passed through molten $AlCl_3$ for 1 h. Mass of Al deposited?',
        hindi: 'पिघले हुए $AlCl_3$ से 1 घंटे तक 5 A की धारा प्रवाहित की जाती है। अल का द्रव्यमान जमा हुआ? **प्राथमिक** (गैर-रिचार्जेबल) - एक बार समाप्त हो जाने पर, त्याग दिया गया:',
          },
          solution: {
            english: `Charge $Q = 5\\times 3600 = 18000$ C. Moles of e⁻ = $18000/96500 = 0.1865$.
Moles of Al = $0.1865/3 = 0.0622$. Mass = $0.0622\\times 27 \\approx 1.68$ g.`,
            hindi: 'चार्ज $Q = 5\\times 3600 = 18000$ C. e⁻ के मोल = $18000/96500 = 0.1865$। Al = के मोल. Mass = g.',
          },
        },
      ],
    },
    {
      id: 'batteries-corrosion',
      title: {
        english: 'Batteries, Fuel Cells & Corrosion',
        hindi: 'बैटरी, ईंधन सेल और संक्षारण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Primary** (non-rechargeable) — once exhausted, discarded:',
        hindi: '**प्राथमिक** (गैर-रिचार्जेबल) - एक बार समाप्त हो जाने पर, त्याग दिया गया:',
            },
            {
              english: '• **Dry cell (Leclanché)**: Zn anode | NH₄Cl + ZnCl₂ paste | MnO₂/C cathode. Anode: Zn → Zn²⁺ + 2e⁻; Cathode: MnO₂ + NH₄⁺ + e⁻ → MnO(OH) + NH₃. EMF ≈ 1.5 V.',
        hindi: '• **शुष्क सेल (लेक्लान्चे)**: Zn एनोड | NH₄Cl + ZnCl₂ पेस्ट | MnO₂/C कैथोड। एनोड: Zn → Zn²⁺ + 2e⁻; कैथोड: MnO₂ + NH₄⁺ + e⁻ → MnO(OH) + NH₃। ईएमएफ ≈ 1.5 वी.',
            },
            {
              english: '• **Mercury cell**: Zn(Hg) | KOH + ZnO | HgO/C. Constant 1.35 V; pacemakers, hearing aids.',
        hindi: '• **बुध कोशिका**: Zn(Hg) | KOH + ZnO | HgO/C. लगातार 1.35 वी; पेसमेकर, श्रवण यंत्र।',
            },
            {
              english: '**Secondary** (rechargeable):',
        hindi: '**माध्यमिक** (रिचार्जेबल):',
            },
            {
              english: '• **Lead-acid**: Pb | H₂SO₄ (38 %) | PbO₂. Discharge: $Pb + PbO_2 + 2H_2SO_4 \\to 2PbSO_4 + 2H_2O$. EMF 2 V/cell; 6 cells in series → 12 V car battery.',
        hindi: '• **सीसा-अम्ल**: Pb | H₂SO₄ (38 %) | PbO₂. डिस्चार्ज: $Pb + PbO_2 + 2H_2SO_4 \\to 2PbSO_4 + 2H_2O$. ईएमएफ 2 वी/सेल; श्रृंखला में 6 सेल → 12 V कार बैटरी।',
            },
            {
              english: '• **Ni-Cd**: Cd anode, NiO(OH) cathode in KOH; EMF 1.4 V; longer life than lead-acid.',
        hindi: '• **Ni-Cd**: Cd एनोड, KOH में NiO(OH) कैथोड; ईएमएफ 1.4 वी; सीसा-एसिड की तुलना में लंबा जीवन।',
            },
            {
              english: '**Fuel cell** (e.g. $H_2$–$O_2$ used in Apollo): porous C electrodes; KOH electrolyte. Anode: $H_2 + 2OH^- \\to 2H_2O + 2e^-$; Cathode: $O_2 + 2H_2O + 4e^- \\to 4OH^-$. ~70 % efficient; clean.',
        hindi: '**ईंधन सेल** (जैसे अपोलो में प्रयुक्त $H_2$–$O_2$): छिद्रपूर्ण सी इलेक्ट्रोड; KOH इलेक्ट्रोलाइट. एनोड: $H_2 + 2OH^- \\to 2H_2O + 2e^-$; कैथोड: $O_2 + 2H_2O + 4e^- \\to 4OH^-$. ~70% कुशल; साफ।',
            },
          ],
        },
        {
          type: 'definition',
          term: {
            english: 'Corrosion',
        hindi: 'संक्षारण',
          },
          body: {
            english: 'Slow electrochemical oxidation of a metal by the environment. Iron rusting requires both O₂ and H₂O. At anode (Fe → Fe²⁺ + 2e⁻); at cathode O₂ + 4H⁺ + 4e⁻ → 2H₂O. Fe²⁺ further oxidised to $Fe_2O_3 \\cdot xH_2O$ (rust). Prevention: barrier coating, galvanising (Zn — sacrificial), tin plating, cathodic protection (Mg/Zn block), alloying (stainless steel).',
        hindi: 'पर्यावरण द्वारा किसी धातु का धीमा विद्युत रासायनिक ऑक्सीकरण। लोहे में जंग लगने के लिए O₂ और H₂O दोनों की आवश्यकता होती है। एनोड पर (Fe → Fe²⁺ + 2e⁻); कैथोड O₂ + 4H⁺ + 4e⁻ → 2H₂O पर। Fe²⁺ आगे चलकर $Fe_2O_3 \\cdot xH_2O$ (जंग) में ऑक्सीकृत हो गया। रोकथाम: बैरियर कोटिंग, गैल्वनाइजिंग (Zn - बलि), टिन चढ़ाना, कैथोडिक संरक्षण (Mg/Zn ब्लॉक), मिश्रधातु (स्टेनलेस स्टील)।',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'E_{\\text{cell}} = E^\\circ_{\\text{cell}} - \\dfrac{0.0591}{n}\\log Q',
      desc: {
        english: 'Nernst (298 K)',
        hindi: 'नर्नस्ट (298 K)',
      },
    },
    {
      latex: '\\Delta G^\\circ = -nFE^\\circ = -RT\\ln K',
      desc: {
        english: 'Thermodynamics',
        hindi: 'ऊष्मागतिकी',
      },
    },
    {
      latex: '\\Lambda_m = \\dfrac{1000\\,\\kappa}{c}',
      desc: {
        english: 'Molar conductivity',
        hindi: 'मोलर चालकता',
      },
    },
    {
      latex: '\\Lambda_m^\\circ = \\nu_+ \\lambda_+^\\circ + \\nu_- \\lambda_-^\\circ',
      desc: {
        english: 'Kohlrausch\'s law',
        hindi: 'कोहलराउश का नियम',
      },
    },
    {
      latex: '\\alpha = \\Lambda_m/\\Lambda_m^\\circ',
      desc: {
        english: 'Degree of dissociation (weak)',
        hindi: 'पृथक्करण की डिग्री (कमजोर)',
      },
    },
    {
      latex: 'w = \\dfrac{M I t}{n F}',
      desc: {
        english: 'Faraday — mass deposited',
        hindi: 'फैराडे - बड़े पैमाने पर जमा',
      },
    },
    {
      latex: 'F = 96485\\ \\text{C/mol}',
      desc: {
        english: 'Faraday constant',
        hindi: 'फैराडे स्थिरांक',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Faraday',
      problem: {
        english: 'Time required to deposit 1.27 g of Cu by passing 2 A through CuSO₄ solution? (Cu = 63.5)',
        hindi: 'CuSO₄ घोल में 2 A प्रवाहित करके 1.27 ग्राम Cu जमा करने में लगने वाला समय? (Cu = 63.5)',
      },
      answer: {
        english: '≈ 1930 s (32 min 10 s)',
        hindi: '1930 सेकंड (32 मिनट 10 सेकंड)',
      },
      solution: {
        english: 'Moles Cu = 0.02; moles e⁻ = 0.04; $t = 0.04\\times 96500/2 = 1930$ s.',
        hindi: 'मोल्स Cu = 0.02; मोल्स ई⁻ = 0.04; $t = 0.04\\times 96500/2 = 1930$ s.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Nernst',
      problem: {
        english: 'For Zn|Zn²⁺(0.1 M) || Cu²⁺(1 M)|Cu, $E^\\circ = 1.10$ V. EMF?',
        hindi: 'Zn|Zn²⁺(0.1 M) || के लिए Cu²⁺(1 M)|Cu, $E^\\circ = 1.10$ V. ईएमएफ?',
      },
      answer: {
        english: '≈ 1.13 V',
        hindi: '≈ 1.13 वी',
      },
      solution: {
        english: '$E = 1.10 - (0.0591/2)\\log(0.1/1) = 1.10 + 0.0296 = 1.13$ V.',
        hindi: '$ई = 1.10 - (0.0591/2)\\log(0.1/1) = 1.10 + 0.0296 = 1.13$ वी।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Conductance',
      problem: {
        english: '$\\Lambda_m^\\circ$ of CH₃COOH using Kohlrausch from $\\Lambda_m^\\circ$ of CH₃COONa, HCl and NaCl?',
        hindi: 'CH₃COONa, HCl और NaCl के $\\Lambda_m^\\circ$ से Kohlrausch का उपयोग करके CH₃COOH का $\\Lambda_m^\\circ$?',
      },
      answer: {
        english: '$\\Lambda_m^\\circ$(HAc) = $\\Lambda_m^\\circ$(NaAc) + $\\Lambda_m^\\circ$(HCl) − $\\Lambda_m^\\circ$(NaCl)',
        hindi: '$\\Lambda_m^\\circ$(HAc) = $\\Lambda_m^\\circ$(NaAc) + $\\Lambda_m^\\circ$(HCl) − $\\Lambda_m^\\circ$(NaCl)',
      },
      solution: {
        english: 'Add/subtract ionic contributions.',
        hindi: 'Add/subtract आयनिक योगदान।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Battery',
      problem: {
        english: 'Cathode of Leclanché (dry) cell:',
        hindi: 'लेकलेंच (शुष्क) सेल का कैथोड:',
      },
      answer: {
        english: 'Graphite rod surrounded by MnO₂ + C',
        hindi: 'ग्रेफाइट की छड़ MnO₂ + C से घिरी हुई है',
      },
      solution: {
        english: 'MnO₂ is reduced to MnO(OH).',
        hindi: 'MnO₂ को MnO(OH) में घटा दिया गया है।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Corrosion',
      problem: {
        english: 'Galvanisation protects Fe from rusting because Zn:',
        hindi: 'गैल्वनीकरण Fe को जंग लगने से बचाता है क्योंकि Zn:',
      },
      answer: {
        english: 'Acts as a sacrificial anode (more easily oxidised)',
        hindi: 'एक बलि एनोड के रूप में कार्य करता है (अधिक आसानी से ऑक्सीकृत)',
      },
      solution: {
        english: '$E^\\circ(Zn^{2+}/Zn) = -0.76 < E^\\circ(Fe^{2+}/Fe) = -0.44$ V.',
        hindi: '$E^\\circ(Zn^{2+}/Zn) = -0.76 < E^\\circ(Fe^{2+}/Fe) = -0.44$ V.',
      },
    },
  ],
  next: {
    slug: 'chemical-kinetics',
    title: 'Chemical Kinetics',
  },
};

export default chapter;
